import React, { useEffect, useMemo, useState } from 'react';
import { Card, Icon, Intent } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { useHistory, useParams } from 'react-router-dom';
import { useObserver } from 'mobx-react-lite';
import { DurationInputArg2 } from 'moment';
import MainContainer from './MainContainer';
import { dataSyncMetricsStore } from '../../stores/DataSyncMetricsStore';
import DataSyncDetailsStore from '../../stores/DataSyncDetailsStore';
import { directoryStore } from '../../stores/DirectoryStore';
import { DataSyncHealthStatus } from '../../types/DataSyncMetrics';
import {
  MetricsGranularity,
  normalizeRangeForGranularity,
} from '../../lib/metricsUtils';
import SparklineChart from './components/SparklineChart';
import DetailMetricsChart from './components/DetailMetricsChart';
import { LoadingIndicator } from '../../components/LoadingIndicator';
import { showToasts } from '../../lib/toaster';
import { HostPageMessenger } from '../../lib/HostPageMessenger';
import { formatInteger, formatSignedInteger, formatSignedPercent } from './utils/analyticsMath';
import { buildAnalyticsViewModel, StageCardKey } from './utils/analyticsViewModel';
import { getDestinationDisplayInfo, getSourceDisplayInfo } from './utils/endpointDisplayInfo';
import { parseDateToEpoch } from './utils/dateRangeEpoch';
import { getPresetRangeEpoch } from './utils/presetRangeEpoch';
import AnalyticsHeader from './components/AnalyticsHeader';
import AnalyticsOverviewCards from './components/AnalyticsOverviewCards';
import styles from './DataSyncAnalyticsPage.module.sass';

const RANGE_OPTIONS: Array<{ key: string; unit: DurationInputArg2; quantity: number; label: string }> = [
  { key: 'days_7', unit: 'days', quantity: 7, label: 'Past 7 days' },
  { key: 'days_15', unit: 'days', quantity: 15, label: 'Past 15 days' },
  { key: 'days_30', unit: 'days', quantity: 30, label: 'Past 30 days' },
];

const DAY_IN_SECONDS = 24 * 60 * 60;

const granularityStepSeconds = (granularity: MetricsGranularity): number => (
  granularity === 'METRICS_GRANULARITY_HOURLY' ? 60 * 60 : DAY_IN_SECONDS
);

interface TimeFrameFilter {
  timeFrame: string;
  start: string | null;
  end: string | null;
}

const healthIntent = (status: DataSyncHealthStatus): Intent => {
  if (status === DataSyncHealthStatus.FAILING) {
    return Intent.DANGER;
  }
  if (status === DataSyncHealthStatus.WARNING) {
    return Intent.WARNING;
  }
  return Intent.SUCCESS;
};

const healthLabel = (status: DataSyncHealthStatus): string => {
  if (status === DataSyncHealthStatus.HEALTHY) {
    return 'Healthy';
  }
  if (status === DataSyncHealthStatus.WARNING) {
    return 'Warning';
  }
  return 'Failing';
};

export const DataSyncAnalyticsPage = () => {
  const { dataSyncId } = useParams<{ dataSyncId: string }>();
  const history = useHistory();
  const [timeframeFilter, setTimeframeFilter] = useState<TimeFrameFilter>({
    timeFrame: 'days_7',
    start: null,
    end: null,
  });
  const [selectedStageKey, setSelectedStageKey] = useState<StageCardKey>('received');
  const [detailsStore] = useState(() => new DataSyncDetailsStore(dataSyncId));
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [dayTick, setDayTick] = useState(() => Math.floor(Date.now() / DAY_IN_SECONDS / 1000));

  // Reset the "has rendered once" flag when the user navigates between syncs
  // so the new sync shows the loading state instead of flashing the previous
  // sync's cached numbers while its own fetch is in flight.
  useEffect(() => {
    setHasLoadedOnce(false);
  }, [dataSyncId]);

  /**
   * Polls the current UTC day every minute and bumps `dayTick` whenever it
   * advances. This forces `rangeEpochs` (which depends on `dayTick`) to
   * recompute when a user leaves the page open across midnight UTC, so a
   * "Past 7 days" preset doesn't get stuck on yesterday's window.
   *
   * The setter is a no-op when the day hasn't changed, so re-renders are
   * limited to actual day rollovers — at most once per 24 hours per session.
   */
  useEffect(() => {
    const intervalMs = 60 * 1000;
    const intervalId = window.setInterval(() => {
      const nextTick = Math.floor(Date.now() / DAY_IN_SECONDS / 1000);
      setDayTick((current) => (current === nextTick ? current : nextTick));
    }, intervalMs);
    return () => window.clearInterval(intervalId);
  }, []);

  const rangeEpochs = useMemo(() => {
    const customFrom = parseDateToEpoch(timeframeFilter.start, 'start');
    const customTo = parseDateToEpoch(timeframeFilter.end, 'end');

    if (customFrom && customTo) {
      return {
        fromEpoch: customFrom,
        toEpoch: customTo,
      };
    }

    const dayCountByKey: Record<string, number> = {
      days_7: 7,
      days_15: 15,
      days_30: 30,
    };

    const days = dayCountByKey[timeframeFilter.timeFrame] || 7;
    return getPresetRangeEpoch(days);
    // dayTick is included so preset ranges advance when the UTC day rolls over.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframeFilter.end, timeframeFilter.start, timeframeFilter.timeFrame, dayTick]);

  const analyticsGranularity = useMemo<MetricsGranularity>(() => {
    const duration = rangeEpochs.toEpoch - rangeEpochs.fromEpoch;
    return duration <= 2 * DAY_IN_SECONDS
      ? 'METRICS_GRANULARITY_HOURLY'
      : 'METRICS_GRANULARITY_DAILY';
  }, [rangeEpochs.fromEpoch, rangeEpochs.toEpoch]);

  const getActivityLogsTimeFrame = (): string => {
    if (timeframeFilter.timeFrame === 'days_7') {
      return 'days_7';
    }

    return 'custom_range';
  };

  const getActivityLogsHashForRange = () => {
    const activityLogsTimeFrame = getActivityLogsTimeFrame();
    return `/activity_logs?rowStart=1&timeFrame=${activityLogsTimeFrame}&start=${rangeEpochs.fromEpoch}&end=${rangeEpochs.toEpoch}&activity=data_sync_error`;
  };

  const previousRangeEpochs = useMemo(() => {
    const aligned = normalizeRangeForGranularity(
      rangeEpochs.fromEpoch,
      rangeEpochs.toEpoch,
      analyticsGranularity,
    );
    const step = granularityStepSeconds(analyticsGranularity);
    const currentDuration = aligned.toEpoch - aligned.fromEpoch;
    const previousToEpoch = aligned.fromEpoch - step;
    const previousFromEpoch = previousToEpoch - currentDuration;
    return { fromEpoch: previousFromEpoch, toEpoch: previousToEpoch };
  }, [analyticsGranularity, rangeEpochs.fromEpoch, rangeEpochs.toEpoch]);

  useEffect(() => {
    let cancelled = false;

    const fetchMetrics = async () => {
      try {
        if (!directoryStore.trackerId) {
          return;
        }

        await Promise.all([
          dataSyncMetricsStore.fetchMetrics(directoryStore.trackerId, [dataSyncId], {
            fromEpoch: rangeEpochs.fromEpoch,
            toEpoch: rangeEpochs.toEpoch,
            granularity: analyticsGranularity,
          }),
          dataSyncMetricsStore.fetchMetrics(directoryStore.trackerId, [dataSyncId], {
            fromEpoch: previousRangeEpochs.fromEpoch,
            toEpoch: previousRangeEpochs.toEpoch,
            granularity: analyticsGranularity,
          }),
        ]);
      } catch (e) {
        if (!cancelled) {
          showToasts([{ intent: 'danger', message: `Failed to load analytics. ${e}` }]);
        }
      }
    };

    fetchMetrics();

    return () => {
      cancelled = true;
    };
  }, [analyticsGranularity, dataSyncId, rangeEpochs, previousRangeEpochs]);

  return useObserver(() => {
    const trackerId = directoryStore.trackerId;
    const dataSync = detailsStore.dataSync;
    const currentQuery = trackerId
      ? {
        trackerId,
        fromEpoch: rangeEpochs.fromEpoch,
        toEpoch: rangeEpochs.toEpoch,
        granularity: analyticsGranularity,
      }
      : undefined;
    const previousQuery = trackerId
      ? {
        trackerId,
        fromEpoch: previousRangeEpochs.fromEpoch,
        toEpoch: previousRangeEpochs.toEpoch,
        granularity: analyticsGranularity,
      }
      : undefined;
    const summary = currentQuery
      ? dataSyncMetricsStore.getSummary(dataSyncId, currentQuery)
      : undefined;
    const buckets = currentQuery
      ? dataSyncMetricsStore.getBuckets(dataSyncId, currentQuery)
      : [];
    const previousBuckets = previousQuery
      ? dataSyncMetricsStore.getBuckets(dataSyncId, previousQuery)
      : [];
    const currentLoading = currentQuery
      ? dataSyncMetricsStore.isLoadingFor(dataSyncId, currentQuery)
      : false;
    const previousLoading = previousQuery
      ? dataSyncMetricsStore.isLoadingFor(dataSyncId, previousQuery)
      : false;
    const loading = !trackerId
      || detailsStore.loading
      || !dataSync
      || (currentLoading && !summary)
      || (previousLoading && previousBuckets.length === 0);

    if (!loading && !hasLoadedOnce) {
      setHasLoadedOnce(true);
    }

    const normalizedRange = normalizeRangeForGranularity(
      rangeEpochs.fromEpoch,
      rangeEpochs.toEpoch,
      analyticsGranularity
    );
    const {
      cards,
      totalReceived,
      totalDelivered,
      receivedTrend,
      deliveredTrend,
      receivedSeries,
      deliveredSeries,
      detailSeries,
      detailTitle,
      detailDescription,
      detailValueLabel,
      detailPrimaryValue,
      detailSecondaryLine,
    } = buildAnalyticsViewModel({
      buckets,
      previousBuckets,
      selectedStageKey,
      fromEpoch: normalizedRange.fromEpoch,
      toEpoch: normalizedRange.toEpoch,
      granularity: analyticsGranularity,
    });

    const sourceDisplayInfo = getSourceDisplayInfo(dataSync);
    const destinationDisplayInfo = getDestinationDisplayInfo(dataSync);

    if (loading && !hasLoadedOnce) {
      return (
        <MainContainer fixedHeight={true}>
          <LoadingIndicator />
        </MainContainer>
      );
    }

    return (
      <MainContainer scrollableContent={true}>
        <div className={styles.container}>
          <AnalyticsHeader
            dataSyncName={dataSync?.name || 'Data Sync'}
            healthIntent={summary ? healthIntent(summary.healthStatus) : undefined}
            healthLabel={summary ? healthLabel(summary.healthStatus) : undefined}
            dateRangeOptions={RANGE_OPTIONS}
            timeframeKey={timeframeFilter.timeFrame}
            timeframeValue={timeframeFilter.start && timeframeFilter.end
              ? [timeframeFilter.start, timeframeFilter.end]
              : undefined}
            analyticsTimeframeFilterClassName={styles.analyticsTimeframeFilter}
            onBack={() => history.push('/data_syncs')}
            onViewActivityLogs={() => {
              HostPageMessenger.send('setWindowLocation', {
                hash: getActivityLogsHashForRange(),
              });
            }}
            onTimeframeChange={(option: { key: string; start?: string; end?: string }) => {
              setTimeframeFilter((previous) => {
                const next = {
                  timeFrame: option.key,
                  start: option.start || null,
                  end: option.end || null,
                };

                if (
                  previous.timeFrame === next.timeFrame
                  && previous.start === next.start
                  && previous.end === next.end
                ) {
                  return previous;
                }

                return next;
              });
            }}
          />

          <h2 className={styles.sectionTitle}>Overview</h2>

          <AnalyticsOverviewCards
            selectedStageKey={selectedStageKey}
            sourceDisplayInfo={sourceDisplayInfo}
            destinationDisplayInfo={destinationDisplayInfo}
            totalReceived={totalReceived}
            totalDelivered={totalDelivered}
            receivedTrend={receivedTrend}
            deliveredTrend={deliveredTrend}
            receivedSeries={receivedSeries}
            deliveredSeries={deliveredSeries}
            formatInteger={formatInteger}
            onSelectStage={setSelectedStageKey}
          />

          <div className={styles.stagesLabel}>Errors/warnings by stage</div>
          <div className={styles.stageGrid}>
            {cards.filter((card) => card.key !== 'received' && card.key !== 'delivered').map((stage, index, array) => (
              <Card
                key={stage.name}
                className={`${styles.stageCard} ${styles.stageCardOrdered} ${selectedStageKey === stage.key ? styles.activeCard : ''}`}
                onClick={() => setSelectedStageKey(stage.key)}
                role="button"
                tabIndex={0}
                onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSelectedStageKey(stage.key);
                  }
                }}
              >
                <div className={styles.cardLabel}>{stage.name}</div>
                <div className={styles.cardSubValue}>{formatInteger(stage.value)} events with errors/warnings</div>
                <SparklineChart
                  data={stage.series}
                  width="100%"
                  height={45}
                  lineColor={styles.stageSparklineLine}
                  fillColor={styles.stageSparklineFill}
                />
                <div className={styles.trendLine}>
                  Change from previous period: {formatSignedInteger(stage.trend.delta)}
                  {' '}
                  <span className={stage.trend.delta > 0 ? styles.trendNegative : styles.trendPositive}>
                    ({formatSignedPercent(stage.trend.deltaPercent)})
                  </span>
                </div>
                {index < array.length - 1 && (
                  <div className={styles.stageConnector} aria-hidden={true}>
                    <Icon className={styles.stageConnectorIcon} icon={IconNames.ARROW_RIGHT} iconSize={14} />
                  </div>
                )}
              </Card>
            ))}
          </div>

          <h2 className={styles.sectionTitle}>Detail</h2>
          <Card className={styles.detailCard}>
            <div className={styles.detailLeft}>
              <h3>{detailTitle}</h3>
              <p>{detailDescription}</p>
              <strong>{formatInteger(detailPrimaryValue)} {detailValueLabel}</strong>
              <p>{detailSecondaryLine}</p>
            </div>
            <div className={styles.detailRight}>
              <div className={styles.detailMetric}>{formatInteger(detailPrimaryValue)}</div>
              <DetailMetricsChart
                data={detailSeries}
                granularity={analyticsGranularity === 'METRICS_GRANULARITY_HOURLY' ? 'hourly' : 'daily'}
                variant={selectedStageKey === 'received' || selectedStageKey === 'delivered' ? 'success' : 'error'}
              />
            </div>
          </Card>
        </div>
      </MainContainer>
    );
  });
};

export default DataSyncAnalyticsPage;
